import { useNavigate } from 'react-router';
import axios, { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { AccountFillStageEnum } from '@thinknet-gmbh/platform-common';
import { PublicClientApplication } from '@azure/msal-browser';
import { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';

import { showNotification, NOTIFICATION_TYPE } from '@thinknet-gmbh/platform-common-ui';

import { useAuth } from 'src/hooks/auth.hooks';
import { ROUTES, SUB_ROUTES } from 'src/components/app/app-routes.constants';

import {
  ILoginDataInterface,
  IAuthAxiosInterface,
  IAuthRefreshInterface,
} from './auth.axios.interfaces';
import { MSAL_CONFIG, AXIOS_AUTH_CONFIG, MSAL_TOKEN_CONFIG } from './auth.axios.constants';

export const msalInstance = new PublicClientApplication(MSAL_CONFIG);
const axiosInstance = axios.create(AXIOS_AUTH_CONFIG);

export const authRefreshToken = ({ onFinish, setSessionState }: IAuthRefreshInterface) => {
  const refreshTokenFn = () => {
    const refreshToken = localStorage.getItem('refreshToken');
    const tokenTime = localStorage.getItem('tokenTime') || '';
    const refreshInterval = (parseInt(tokenTime) - 200) * 1000;

    if (refreshToken) {
      const options = { headers: { Refresh: refreshToken } };

      return axiosInstance
        .get('refresh', options)
        .then(({ data }: AxiosResponse) => {
          const { accessToken } = data;

          if (accessToken) {
            localStorage.setItem('accessToken', accessToken);

            setSessionState({ accessToken, isAuthenticated: true, redirectPath: ROUTES.dashboard });

            setTimeout(refreshTokenFn, refreshInterval);
          }
        })
        .catch(() => {
          setSessionState({ accessToken: '', isAuthenticated: false, redirectPath: ROUTES.auth });
        })
        .finally(onFinish);
    }

    return onFinish();
  };

  refreshTokenFn();
};

export const useAuthAxiosInstance = ({
  onFinish,
  externalAccessToken,
}: IAuthAxiosInterface = {}) => {
  const { setAuthSession } = useAuth();
  const navigate = useNavigate();
  const { t: globalExceptionsT } = useTranslation('common', { keyPrefix: 'exceptions' });
  const { t: successesT } = useTranslation('auth', { keyPrefix: 'successes' });
  const { t: exceptionsT } = useTranslation('auth', { keyPrefix: 'exceptions' });

  const exceptionHandler = (error: { response: AxiosResponse }) => {
    const { message, statusCode } = error.response?.data || {};
    const notificationMessage =
      statusCode === 406 ? exceptionsT(message) : globalExceptionsT('internalError');

    showNotification({
      type: NOTIFICATION_TYPE.error,
      message: notificationMessage,
      isIcon: false,
    });
  };

  const successAuthHandler = ({ data }: AxiosResponse) => {
    const { accessTokenTime, accessToken, refreshToken, accountFillStage } = data;

    setAuthSession({ accessToken, refreshToken, accessTokenTime });

    if (accountFillStage === AccountFillStageEnum.COMPLETED) {
      return navigate(ROUTES.dashboard);
    }

    navigate(SUB_ROUTES.authWelcome);
  };

  return {
    verify: ({ email, password }: ILoginDataInterface) =>
      axiosInstance
        .post('/verification', { email, password })
        .then(({ data }: AxiosResponse) => {
          setAuthSession({ accessToken: data.accessToken });
          navigate(SUB_ROUTES.authValidation);
        })
        .catch(exceptionHandler),

    confirmEmailChange: (token: string) => {
      const options = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

      axiosInstance.post('/confirmEmailChange', { token }, options).then(() =>
        showNotification({
          type: NOTIFICATION_TYPE.success,
          message: successesT('emailAddressChanged'),
        })
      );
    },

    validate(verificationCode: string) {
      const options = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

      return axiosInstance
        .post('/validation', { verificationCode }, options)
        .then(successAuthHandler)
        .catch(exceptionHandler);
    },

    resendCode() {
      const options = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

      return axiosInstance
        .post('/resendCode', {}, options)
        .then((response: AxiosResponse) => {
          const { statusCode } = response.data;

          if (statusCode === 200) {
            return showNotification({
              type: NOTIFICATION_TYPE.success,
              message: successesT('codeResend'),
            });
          }

          return exceptionHandler({ response });
        })
        .catch(exceptionHandler);
    },

    forgotPassword: ({ email }: ILoginDataInterface) =>
      axiosInstance
        .post('/forgotPassword', { email })
        .then(({ data }: AxiosResponse) => {
          const { statusCode } = data;

          if (statusCode === 200) {
            onFinish && onFinish();
          }
        })
        .catch(exceptionHandler),

    resetPassword({ email, password }: ILoginDataInterface) {
      const options = { headers: { Authorization: `Bearer ${externalAccessToken}` } };

      return axiosInstance
        .patch('/resetPassword', { email, password }, options)
        .then(({ data }: AxiosResponse) => {
          setAuthSession({ accessToken: data.accessToken });
          navigate(SUB_ROUTES.authValidation);
        })
        .catch(exceptionHandler);
    },

    googleAuth: (response: GoogleLoginResponse | GoogleLoginResponseOffline) => {
      if ('accessToken' in response) {
        const accessToken = response.accessToken;

        return axiosInstance
          .post('/googleAuth', { token: accessToken })
          .then(successAuthHandler)
          .catch(exceptionHandler);
      }
    },

    microsoftAuth: async () => {
      try {
        const { accessToken } = await msalInstance.loginPopup(MSAL_TOKEN_CONFIG);
        const options = { headers: { Authorization: `Bearer ${accessToken}` } };

        axiosInstance
          .post('/microsoftAuth', {}, options)
          .then(successAuthHandler)
          .catch(exceptionHandler);
      } catch (error: any) {
        showNotification({
          type: NOTIFICATION_TYPE.error,
          message: globalExceptionsT('internalError'),
          ...(error && { description: error.errorMessage }),
        });
      }
    },
  };
};
